import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, FormSection, Icon, Paragraph, toastError, toastSuccess } from 'admination-react'
import { ApiError } from '@/core/api/http'
import { DateField } from '@/core/components/form/DateField'
import { SelectField } from '@/core/components/form/Select'
import { TextField } from '@/core/components/form/TextField'
import { AskModal } from '@/core/components/ui/ask-modal'
import { formatDatum, todayIso } from '@/core/lib/dates'
import { useApp } from '@/core/state/appStore'
import { RUECKZUG_GRUENDE, rueckzugGrundText, type RueckzugGrundArt } from '@/core/types'
import { isEingeschraenkt } from '../../api/teilnehmerakte-staff'
import { GRUND_MAX, isZurueckgezogen, zurueckziehen } from '../../api/rueckzug'

export function RueckzugSection({ coachingId, onEingeschraenkt }: { coachingId: string; onEingeschraenkt: () => void }) {
  const { t } = useTranslation('vergabeStaff')
  const { dispatch, reloadDashboard } = useApp()
  const heute = todayIso()
  const [am, setAm] = useState(heute)
  const [grundArt, setGrundArt] = useState<RueckzugGrundArt | ''>('')
  const [grund, setGrund] = useState('')
  const sonstiges = grundArt === 'sonstiges'
  const grundLabel = (art: RueckzugGrundArt) => t(`profil.rueckzug.gruende.${art}`)
  const [fragen, setFragen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [zukunft, setZukunft] = useState(false)
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])
  const inZukunft = zukunft || am > heute

  function schliessen() {
    dispatch({ type: 'CLOSE' })
    reloadDashboard()
  }

  async function bestaetigen() {
    if (!grundArt) return
    setBusy(true)
    try {
      await zurueckziehen(coachingId, { am, grund_art: grundArt, grund })
      toastSuccess(t('profil.rueckzug.erfolg'))
      schliessen()
    } catch (error) {
      if (isZurueckgezogen(error)) {
        toastError(t('profil.rueckzug.bereits'))
        schliessen()
      } else if (isEingeschraenkt(error)) onEingeschraenkt()
      else if (error instanceof ApiError && error.status === 422) setZukunft(true)
      else if (error instanceof ApiError && error.status === 403) toastError(t('profil.rueckzug.verboten'))
      else toastError(t('profil.rueckzug.fehler'))
    } finally {
      if (mounted.current) {
        setBusy(false)
        setFragen(false)
      }
    }
  }

  return (
    <FormSection label={t('profil.rueckzug.section')}>
      <Paragraph variant="muted">{t('profil.rueckzug.hint')}</Paragraph>
      <DateField
        label={t('profil.rueckzug.am')}
        isoValue={am}
        maxIso={heute}
        pastYears={1}
        invalid={inZukunft}
        errorMessage={inZukunft ? t('profil.rueckzug.zukunft') : undefined}
        onChange={(iso) => {
          setAm(iso)
          setZukunft(false)
        }}
      />
      <SelectField
        label={t('profil.rueckzug.grund')}
        options={[...RUECKZUG_GRUENDE]}
        optionLabel={(art) => grundLabel(art as RueckzugGrundArt)}
        value={grundArt}
        required
        placeholder={t('profil.rueckzug.bitteWaehlen')}
        onChange={(art) => setGrundArt(art as RueckzugGrundArt | '')}
      />
      {sonstiges ? (
        <TextField
          label={t('profil.rueckzug.sonstiges')}
          value={grund}
          required
          maxLength={GRUND_MAX}
          hint={{ tone: 'info', text: t('profil.rueckzug.zeichen', { anzahl: grund.length, max: GRUND_MAX }) }}
          onChange={setGrund}
        />
      ) : null}
      <div>
        <Button
          data-testid="zurueckziehen"
          disabled={busy || !am || inZukunft || !grundArt || (sonstiges && !grund.trim())}
          onClick={() => setFragen(true)}
        >
          <Icon name="i-logout" />
          {t('profil.rueckzug.button')}
        </Button>
      </div>
      <AskModal
        open={fragen}
        icon="i-person"
        iconStroke
        variant="is-warning"
        question={t('profil.rueckzug.title')}
        description={[
          t('profil.rueckzug.text', { am: formatDatum(am) }),
          grundArt
            ? t('profil.rueckzug.textGrund', { grund: rueckzugGrundText(grundLabel(grundArt), sonstiges ? grund.trim() : null) })
            : null,
        ]
          .filter(Boolean)
          .join(' ')}
        confirmLabel={t('profil.rueckzug.confirm')}
        cancelLabel={t('profil.rueckzug.cancel')}
        busy={busy}
        onConfirm={() => void bestaetigen()}
        onCancel={() => setFragen(false)}
      />
    </FormSection>
  )
}
